import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Clock, Tag, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import VehicleShowroom from '../../components/showroom/VehicleShowroom';
import { Button } from '../../components/ui/button';
import { vehiclePublicAPI, rebateAlertsAPI } from '../../lib/api';

// Manager oversight block for an automotive inquiry: the SAME VehicleShowroom the
// buyer & salesperson see (reused, not re-built) + operational status
// (days-on-lot + promotion state) + a manager-only inline promotion authorize.
const fmtIncentive = (inc, t) => {
    if (!inc) return '';
    const kind = inc.kind || inc.type;
    if (kind === 'cash') return `$${(inc.amount ?? inc.value ?? 0).toLocaleString()} ${t('commandCenter.automotive.cashOff', 'cash off')}`;
    if (kind === 'apr') return `${inc.rate ?? inc.amount}% ${t('commandCenter.automotive.apr', 'APR')}`;
    if (kind === 'percent') return `${inc.amount}%`;
    return inc.label || '';
};

export function AutoInquiryVehicle({ cardId, vehicleId, organizationId }) {
    const { t } = useTranslation();
    const [veh, setVeh] = useState(null);
    const [meta, setMeta] = useState({});
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [editing, setEditing] = useState(false);
    const [amt, setAmt] = useState('');

    const loadStatus = useCallback(async () => {
        if (!vehicleId || !organizationId) return;
        try { const s = await rebateAlertsAPI.vehicleStatus(vehicleId, organizationId); setStatus(s.data); } catch { /* noop */ }
    }, [vehicleId, organizationId]);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const r = await vehiclePublicAPI.get(cardId);
                if (alive) { setVeh(r.data?.vehicle || null); setMeta({ dealership: r.data?.dealership, salesStatus: r.data?.sales_status }); }
            } catch { /* noop */ }
            await loadStatus();
            if (alive) setLoading(false);
        })();
        return () => { alive = false; };
    }, [cardId, loadStatus]);

    const submitAuthorize = async (incentive) => {
        if (!status?.promotion?.instance_id) return;
        setActing(true);
        try { await rebateAlertsAPI.authorize(status.promotion.instance_id, incentive); setEditing(false); await loadStatus(); }
        catch { toast.error(t('commandCenter.automotive.authorizeFailed', 'Could not authorize the promotion. Please try again.')); }
        setActing(false);
    };
    const withdraw = async () => {
        if (!status?.promotion?.instance_id) return;
        setActing(true);
        try { await rebateAlertsAPI.revoke(status.promotion.instance_id); await loadStatus(); }
        catch { toast.error(t('commandCenter.automotive.withdrawFailed', 'Could not withdraw the promotion. Please try again.')); }
        setActing(false);
    };

    if (loading) return <div className="py-6 text-center" data-testid="cc-inquiry-vehicle-loading"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-400" /></div>;
    if (!veh) return null;

    const promo = status?.promotion;
    const days = status?.days_in_inventory;
    const inc = fmtIncentive(promo?.incentive, t);
    let promoText = t('commandCenter.automotive.promoNone', 'No promotion scheduled');
    let promoColor = 'text-slate-300';
    if (promo?.state === 'authorized') { promoText = t('commandCenter.automotive.promoAuthorized', 'Promotion live: {{inc}}', { inc }); promoColor = 'text-emerald-300'; }
    else if (promo?.state === 'eligible') { promoText = t('commandCenter.automotive.promoEligible', 'Eligible now — awaiting your authorization ({{inc}})', { inc }); promoColor = 'text-amber-300'; }
    else if (promo?.state === 'approaching') { promoText = t('commandCenter.automotive.promoApproaching', 'Next promotion in {{n}} day(s) ({{inc}})', { n: promo.days_until, inc }); promoColor = 'text-sky-300'; }

    return (
        <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950" data-testid="cc-inquiry-vehicle">
            <VehicleShowroom vehicle={veh} dealership={meta.dealership} salesStatus={meta.salesStatus} preview />
            <div className="p-3 border-t border-slate-800 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2 text-sm" data-testid="cc-inquiry-days-on-lot">
                        <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-slate-400">{t('commandCenter.automotive.daysOnLot', 'Days on lot')}:</span>
                        <span className="font-semibold text-white">{days != null ? days : '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-testid="cc-inquiry-promo-status">
                        <Tag className={`w-4 h-4 shrink-0 ${promoColor}`} />
                        <span className={`font-medium ${promoColor}`}>{promoText}</span>
                    </div>
                </div>
                {/* Manager-only inline promotion control — reuses the Promotions-tab authorize/revoke flow */}
                {promo?.state === 'eligible' && promo?.instance_id && !editing && (
                    <Button size="sm" disabled={acting} data-testid="cc-inquiry-authorize-promo"
                        onClick={() => {
                            const kind = promo.incentive?.kind || promo.incentive?.type;
                            if (kind === 'cash') { setAmt(String(promo.incentive?.amount ?? promo.incentive?.value ?? '')); setEditing(true); }
                            else { submitAuthorize(null); }
                        }}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                        {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BadgeCheck className="w-4 h-4 mr-1.5" />}
                        {t('commandCenter.automotive.authorizePromo', 'Authorize promotion — {{inc}}', { inc })}
                    </Button>
                )}
                {promo?.state === 'eligible' && promo?.instance_id && editing && (
                    <div className="space-y-2 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.05] p-3" data-testid="cc-inquiry-authorize-editor">
                        <label className="block text-xs font-semibold text-emerald-700 dark:text-emerald-300">{t('commandCenter.automotive.adjustAmount', 'Cash-off amount to authorize')}</label>
                        <div className="flex items-center gap-1.5">
                            <span className="text-slate-500 dark:text-slate-400 font-semibold">$</span>
                            <input type="number" min="1" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="cc-inquiry-amount-input"
                                className="flex-1 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-emerald-500" />
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" disabled={acting || !(Number(amt) > 0)} data-testid="cc-inquiry-authorize-confirm"
                                onClick={() => submitAuthorize({ kind: 'cash', amount: Number(amt) })}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('commandCenter.automotive.confirmAuthorize', 'Authorize ${{n}} cash off', { n: Number(amt) || 0 })}
                            </Button>
                            <Button size="sm" variant="outline" disabled={acting} onClick={() => setEditing(false)} data-testid="cc-inquiry-authorize-cancel"
                                className="h-8 px-3 text-xs border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">{t('commandCenter.automotive.cancelEdit', 'Cancel')}</Button>
                        </div>
                    </div>
                )}
                {promo?.state === 'authorized' && promo?.instance_id && (
                    <div className="flex items-center gap-2">
                        <span className="flex-1 flex items-center gap-1.5 text-sm font-medium text-emerald-300"><BadgeCheck className="w-4 h-4" />{t('commandCenter.automotive.promoLiveBadge', 'Live on the buyer\u2019s screen')}</span>
                        <Button size="sm" variant="outline" disabled={acting} onClick={withdraw} data-testid="cc-inquiry-withdraw-promo"
                            className="h-8 px-3 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('commandCenter.automotive.withdrawPromo', 'Withdraw')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AutoInquiryVehicle;
